import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe } from "lucide-react";
import { toast } from "sonner";
import ShieldLogo from "./ShieldLogo";
import { scrollToId } from "@/lib/scroll";
import { applyLang, getLang, LANGS } from "@/lib/i18n";

const portalToast = (who) =>
    toast(who === "driver" ? "DRIVER AUTHORIZATION PORTAL" : "FLEET OPERATIONS PORTAL", {
        description: "Portal is staged and ready — production credentials coming soon. Keep the shiny side up.",
    });

const PRODUCT_LINKS = [
    { label: "Live Map", target: "#live-map" },
    { label: "Features", target: "#features" },
    { label: "Manifesto", target: "#manifesto" },
    { label: "Route Specs", target: "#route-specs" },
];

export default function Footer() {
    const navigate = useNavigate();
    const [langOpen, setLangOpen] = useState(false);
    const [lang, setLang] = useState(getLang());
    const [translating, setTranslating] = useState(false);

    const chooseLang = async (code) => {
        if (translating || code === lang) return;
        setTranslating(true);
        await applyLang(code);
        setLang(code);
        setTranslating(false);
        const picked = LANGS.find((l) => l.code === code);
        toast.success(code === "en" ? "ENGLISH RESTORED" : `LANGUAGE SET — ${picked.name}`, {
            description:
                code === "en"
                    ? "Every screen is back to English."
                    : "All screens translated. Turn-by-turn voice speaks it too.",
        });
    };

    return (
        <footer data-testid="site-footer" className="relative overflow-hidden border-t-2 border-[#E5A93C]/30 bg-[#0E0D0C] pb-24 sm:pb-0">{/* extra bottom room on phones so the floating FULL MAP button never sits on footer links (owner report 2026-10-24) */}
            <div className="mx-auto max-w-7xl px-6 pb-10 pt-20 lg:px-16">
                <motion.h2
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="font-display text-[clamp(3.5rem,11vw,10rem)] uppercase leading-[0.85] tracking-wide"
                >
                    <span className="block text-[#F6F2E8]">HAMMER DOWN.</span>
                    <span className="text-stroke-cream block">KEEP ROLLIN'.</span>
                </motion.h2>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="footer-driver-cta"
                        onClick={() => navigate("/login")}
                        className="btn-press font-display border-2 border-[#0E0D0C] bg-[#D32F2F] px-7 py-3.5 text-lg tracking-widest text-[#F6F2E8] shadow-[4px_4px_0px_#E5A93C] hover:bg-[#B71C1C]"
                    >
                        DRIVER LOGIN
                    </button>
                    <button
                        data-testid="footer-fleet-cta"
                        onClick={() => navigate("/fleet")}
                        className="btn-press font-display border-2 border-[#0E0D0C] bg-[#0055A5] px-7 py-3.5 text-lg tracking-widest text-[#F6F2E8] shadow-[4px_4px_0px_#E5A93C] hover:bg-[#003B73]"
                    >
                        FLEET OPERATIONS
                    </button>
                    <span
                        data-testid="footer-system-status"
                        className="font-mono2 flex items-center gap-2 border border-[#2E7D32]/50 px-4 py-2 text-[10px] font-bold tracking-[0.25em] text-[#2E7D32]"
                    >
                        <span className="h-2 w-2 animate-pulse rounded-full bg-[#2E7D32]" />
                        SYSTEM ALL CLEAR — I-95 / I-80 / ROUTE 66 ACTIVE
                    </span>
                </motion.div>

                <div className="mt-16 grid grid-cols-2 gap-10 border-t-2 border-[#24211E] pt-12 md:grid-cols-4">
                    <div className="col-span-2 md:col-span-1">
                        <div className="flex items-center gap-3">
                            <ShieldLogo size={44} />
                            <span className="font-display text-lg leading-none tracking-widest text-[#F6F2E8]">
                                PUT CHA FOOT
                                <br />
                                IN IT DRIVER!
                            </span>
                        </div>
                        <p className="font-mono2 mt-4 text-xs leading-relaxed text-[#8C8377]">
                            The highway navigation app built for 18-wheelers, heavy haulers, and fleet command.
                        </p>
                    </div>
                    <div>
                        <p className="font-mono2 text-xs font-bold tracking-[0.3em] text-[#E5A93C]">PRODUCT</p>
                        <ul className="mt-4 space-y-2">
                            {PRODUCT_LINKS.map((l) => (
                                <li key={l.label}>
                                    <button
                                        data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        onClick={() => scrollToId(l.target)}
                                        className="font-mono2 text-xs text-[#C2C7CE] transition-colors hover:text-[#E5A93C]"
                                    >
                                        {l.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <p className="font-mono2 text-xs font-bold tracking-[0.3em] text-[#E5A93C]">COMPANY</p>
                        <ul className="mt-4 space-y-2">
                            {["About", "Careers", "Press Kit"].map((l) => (
                                <li
                                    key={l}
                                    data-testid={`footer-link-${l.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="font-mono2 cursor-pointer text-xs text-[#C2C7CE] transition-colors hover:text-[#E5A93C]"
                                >
                                    {l}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <p className="font-mono2 text-xs font-bold tracking-[0.3em] text-[#E5A93C]">LEGAL</p>
                        <ul className="mt-4 space-y-2">
                            {["Privacy", "Terms", "DOT Compliance"].map((l) => (
                                <li
                                    key={l}
                                    data-testid={`footer-link-${l.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="font-mono2 cursor-pointer text-xs text-[#C2C7CE] transition-colors hover:text-[#E5A93C]"
                                >
                                    {l}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Owner directive 2026-07-02: translate button on the landing page,
                    languages listed below it — covers every screen + the cab voice.
                    Phone-hardened: full-width button + 2-col chip grid so nothing
                    can ever slide off a small screen (owner report 2026-10-24) */}
                <div data-testid="translate-section" className="mt-10 w-full max-w-full overflow-hidden border-2 border-[#E5A93C]/40 bg-[#161413] p-4">
                    <button
                        data-testid="translate-toggle-button"
                        onClick={() => setLangOpen((o) => !o)}
                        className="btn-press font-display flex w-full items-center justify-center gap-3 border-2 border-[#0E0D0C] bg-[#E5A93C] px-4 py-3 text-base tracking-widest text-[#161413] shadow-[4px_4px_0px_#D32F2F] hover:bg-[#FFC53D] sm:w-auto sm:px-6 sm:text-lg"
                    >
                        <Globe size={20} strokeWidth={2.5} className="shrink-0" />
                        {translating ? "TRANSLATING…" : "TRANSLATE THIS PAGE"}
                    </button>
                    <p className="font-mono2 mt-2 text-[10px] tracking-[0.2em] text-[#8C8377]">
                        EVERY SCREEN + TURN-BY-TURN VOICE — TAP YOUR LANGUAGE BELOW
                    </p>
                    {langOpen && (
                        <div data-testid="translate-language-list" data-no-translate className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                            {LANGS.map((l) => (
                                <button
                                    key={l.code}
                                    data-testid={`translate-lang-${l.code}`}
                                    onClick={() => chooseLang(l.code)}
                                    className={`btn-press font-mono2 w-full border-2 px-3 py-2 text-center text-xs font-bold tracking-[0.15em] sm:w-auto ${
                                        lang === l.code
                                            ? "border-[#E5A93C] bg-[#E5A93C] text-[#161413]"
                                            : "border-[#E5A93C]/40 bg-[#24211E] text-[#F6F2E8] hover:border-[#E5A93C]"
                                    }`}
                                >
                                    {l.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="font-mono2 mt-14 flex flex-wrap items-center justify-between gap-4 border-t-2 border-[#24211E] pt-6 pb-[env(safe-area-inset-bottom)] text-[10px] tracking-[0.25em] text-[#8C8377]">
                    <span>© 2026 PUT CHA FOOT IN IT DRIVER! — ALL ROUTES RESERVED.</span>
                    <a
                        data-testid="footer-support-link"
                        href="mailto:danielturner1974may@gmail.com"
                        className="font-bold tracking-[0.25em] text-[#C2C7CE] underline transition-colors hover:text-[#E5A93C]"
                    >
                        SUPPORT: DANIELTURNER1974MAY@GMAIL.COM
                    </a>
                    <button
                        data-testid="footer-admin-link"
                        onClick={() => navigate("/admin")}
                        className="btn-press border-2 border-[#0E0D0C] bg-[#24211E] px-4 py-1.5 font-bold tracking-[0.25em] text-[#E5A93C] shadow-[3px_3px_0px_#0E0D0C]"
                    >
                        DISPATCH OFFICE
                    </button>
                    <span>MADE FOR THE AMERICAN HIGHWAY ★ 10-4, GOOD BUDDY</span>
                </div>

                {/* Owner directive 2026-10-21: small-font copyright line, bottom of the landing page */}
                <div
                    data-testid="footer-copyright"
                    className="font-mono2 mt-6 text-center text-[9px] tracking-[0.3em] text-[#8C8377]/70"
                >
                    ALL RIGHTS RESERVED — COPYRIGHT © 2026 DANIEL TURNER
                </div>
            </div>
        </footer>
    );
}
